import { addDays, format } from 'date-fns';
import { DOORS_DEFAULTS_REQUIRED, DOORS_WINDOW_REQUIRED } from '@/lib/labels';

/** `yyyy-MM-ddTHH:mm`, the value of a `datetime-local` input. */
const LOCAL_INPUT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
/** `yyyy-MM-dd`, the value of a `date` input. */
const DAY_INPUT = /^\d{4}-\d{2}-\d{2}$/;
const HH_MM = /^\d{2}:\d{2}/;

/**
 * A night's doors window is required. Why `open`/`close` (both `datetime-local`
 * values) are not a usable window, or null when they are. Same-format local
 * strings compare chronologically, so no Date parsing is needed.
 */
export function doorsWindowProblem(open: string, close: string): string | null {
  if (!LOCAL_INPUT.test(open) || !LOCAL_INPUT.test(close)) return DOORS_WINDOW_REQUIRED;
  if (close <= open) return 'Doors close must be after doors open';
  return null;
}

/**
 * A blueprint's default doors (`HH:MM` times of day, both required).
 * Null when usable.
 */
export function doorsDefaultsProblem(open: string, close: string): string | null {
  if (!HH_MM.test(open) || !HH_MM.test(close)) return DOORS_DEFAULTS_REQUIRED;
  return null;
}

/**
 * Project a blueprint's `HH:MM` doors defaults onto the calendar date of
 * `day` (a `date` input value, `yyyy-MM-dd`). Mirrors Go's `ProjectDoorsWindow`:
 * close is the next day when it is not after open (16:00 to 04:00).
 */
export function projectDoorsWindow(
  doorsOpen: string,
  doorsClose: string,
  day: string,
): { open: string; close: string } {
  if (doorsDefaultsProblem(doorsOpen, doorsClose) || !DAY_INPUT.test(day)) {
    return { open: '', close: '' };
  }
  const open = doorsOpen.slice(0, 5);
  const close = doorsClose.slice(0, 5);
  const closeDay =
    close > open ? day : format(addDays(new Date(`${day}T12:00`), 1), 'yyyy-MM-dd');
  return { open: `${day}T${open}`, close: `${closeDay}T${close}` };
}
